/**
 * Erzeugte Klangbausteine.
 *
 * Alles hier entsteht aus Rauschen und Rechnung, nichts aus Dateien. Zwei
 * Bausteine tragen den Unterwasser-Eindruck:
 *
 *  - **Braunes Rauschen** klingt nach Wasser, weisses nach Zischen. Der
 *    Unterschied ist das Integral: Braunes Rauschen ist aufsummiertes weisses,
 *    seine Energie faellt mit 1/f² zur Hoehe hin ab. Genau das macht den
 *    Unterschied zwischen Meer und Fernsehschnee.
 *
 *  - **Eine erzeugte Impulsantwort** fuer den Hall. Ohne Hall klingt ein Ping
 *    wie ein Piepser; mit Hall klingt er nach einem Signal in einem sehr
 *    grossen, sehr dunklen Raum. Das ist der einzelne groesste Schritt vom
 *    Testton zum Meeresgrund.
 */

/** Braunes Rauschen — die Grundlage des Wasserklangs. */
export function createBrownNoiseBuffer(ctx: AudioContext, seconds = 4): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  let last = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    // Integrieren und daempfen, sonst laeuft der Pegel davon.
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }

  // Die Enden ineinander blenden, damit die Schleife nicht klickt.
  const fade = Math.min(2000, Math.floor(length / 8));
  for (let i = 0; i < fade; i++) {
    const t = i / fade;
    data[i] = (data[i] as number) * t + (data[length - fade + i] as number) * (1 - t);
  }

  return buffer;
}

/** Weisses Rauschen — fuer Gischt und kurze Geraeusche. */
export function createWhiteNoiseBuffer(ctx: AudioContext, seconds = 2): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/**
 * Impulsantwort fuer den Hall.
 *
 * Ein exponentiell abklingender Rauschstoss ist die klassische Naeherung fuer
 * einen grossen Raum. `decay` steuert, wie schnell er verstummt: kleine Werte
 * ergeben eine Halle, grosse einen kurzen Raum.
 *
 * Der Tiefpass ueber die Laufzeit ist das, was ihn *unter Wasser* verortet —
 * hohe Frequenzen werden im Wasser viel schneller geschluckt als tiefe, ein
 * spaeter Nachhall ist deshalb immer dumpfer als der Anschlag.
 */
export function createReverbImpulse(ctx: AudioContext, seconds = 3.5, decay = 2.2): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel);
    // Je Kanal ein eigener Tiefpass-Zustand, sonst klingt der Hall in Mono.
    let filtered = 0;

    for (let i = 0; i < length; i++) {
      const progress = i / length;
      const envelope = (1 - progress) ** decay;

      // Der Tiefpass wird ueber die Zeit immer traeger — der Nachhall verliert
      // die Hoehen, wie Wasser es tut.
      const coefficient = 0.35 - progress * 0.28;
      filtered += coefficient * ((Math.random() * 2 - 1) - filtered);

      data[i] = filtered * envelope;
    }
  }

  return buffer;
}
